import type { DecoratingMethod } from 'common/interfaces/quoter.interface';
import type {
  IDecorDetailInfo,
  IDecoratingMethodSelection,
} from '../common/interfaces/design.interface';
import {
  FALLBACK_PATTERN_COLORBLOCK_IDS,
  getPatternColorblockIds,
  getPatternsAndColorblockTreatmentIds,
  isPatternsAndColorblockLabel,
} from './PrintConfigPatternIdsUtils';
import { LACE_DECORATING_METHOD_ID } from '@/constants/DecorMethods';

const safeParse = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/** Decor rows reach us either already parsed or as the raw JSON column from v3. */
export const parseDecorMethodInfo = (decorMethodInfo: unknown): IDecorDetailInfo[] => {
  const rows = typeof decorMethodInfo === 'string' ? safeParse(decorMethodInfo) : decorMethodInfo;

  if (!Array.isArray(rows)) {
    return [];
  }

  return rows.filter((row): row is IDecorDetailInfo => row !== null && typeof row === 'object');
};

export const isPatternsAndColorblockMethod = (
  id: number | null | undefined,
  name?: string | null
): boolean =>
  id === getPatternColorblockIds().methodId ||
  id === FALLBACK_PATTERN_COLORBLOCK_IDS.methodId ||
  isPatternsAndColorblockLabel(name);

/**
 * The selection is persisted user data, so treat it as untrusted: accept an already-parsed object
 * or a raw JSON string, and return null for anything unusable rather than throwing mid-payload.
 */
export const parseDecoratingMethodSelection = (
  value: IDecoratingMethodSelection | string | null | undefined
): IDecoratingMethodSelection | null => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    const parsed = safeParse(value);

    return parsed !== null && typeof parsed === 'object'
      ? (parsed as IDecoratingMethodSelection)
      : null;
  }

  return typeof value === 'object' ? value : null;
};

/** Part ids for one treatment, dropping unusable ids and de-duplicating so nothing is charged twice. */
const partIdsForTreatment = (
  selection: IDecoratingMethodSelection,
  treatment: number
): number[] => {
  const parts = selection[String(treatment)]?.parts;

  if (!Array.isArray(parts)) {
    return [];
  }

  return parts.reduce<number[]>((ids, part) => {
    const id = Number(part?.id);

    if (Number.isFinite(id) && id > 0 && !ids.includes(id)) {
      ids.push(id);
    }

    return ids;
  }, []);
};

/**
 * Expand a "Patterns and/or Colorblock" row into one quoter entry per treatment. A part chosen for
 * both treatments is sent twice on purpose so the colorblock and pattern rates stack.
 */
export const expandPatternsAndColorblockRow = (
  row: IDecorDetailInfo,
  apparelCategory: string | null
): DecoratingMethod[] => {
  const selection = parseDecoratingMethodSelection(row?.selection);

  if (selection === null) {
    return [];
  }

  const category = row?.productType?.name ?? apparelCategory ?? '';

  return getPatternsAndColorblockTreatmentIds().reduce<DecoratingMethod[]>((entries, treatment) => {
    const parts = partIdsForTreatment(selection, treatment);

    if (!parts.length) {
      return entries;
    }

    entries.push({
      type: getPatternColorblockIds().methodId,
      location: parts,
      parts,
      treatment,
      category,
      numberOfBowsOrStripes: 0,
      color: '',
    });

    return entries;
  }, []);
};

/**
 * Map persisted decor rows to the quoter's `decoratingMethods`.
 *
 * Every method is priced from a single decorating location except "Patterns and/or Colorblock",
 * which has no location at all — it expands to one entry per treatment carrying its garment parts.
 *
 * @param decorMethodInfo - persisted decor rows, parsed or as the raw JSON string
 * @param apparelCategory - garment category, used when a row carries no product type
 */
export const mapDecorMethodInfoToQuoterDecoratingMethods = (
  decorMethodInfo: unknown,
  apparelCategory: string | null = null
): DecoratingMethod[] =>
  parseDecorMethodInfo(decorMethodInfo).reduce<DecoratingMethod[]>((methods, itm) => {
    if (isPatternsAndColorblockMethod(itm?.decoratingMethod?.id, itm?.decoratingMethod?.name)) {
      return methods.concat(expandPatternsAndColorblockRow(itm, apparelCategory));
    }

    const location = itm.stripeLocation?.id ? itm.stripeLocation?.id : itm?.locationType?.id;

    methods.push({
      type: itm?.decoratingMethod?.id,
      location: location ? [location] : [],
      category: itm?.productType?.name ?? '',
      numberOfBowsOrStripes: itm?.numOfDecorItems ?? itm?.numberOfStripes ?? 0,
      /** Lace is the only method whose colour affects the price. */
      color:
        itm?.decoratingMethod?.id === LACE_DECORATING_METHOD_ID
          ? itm?.lacePattern?.name ?? itm?.color ?? ''
          : '',
    });

    return methods;
  }, []);
